import { RealTimeAction } from '@/_core/fluxstore/types';
import { createDebugger } from '@/_core/utils/debug';
import { Activity, StorymapState, Subtask, Task } from '../../types';
import { getCardById, GetNewEmptyActivity, GetNewEmptySubtask, GetNewEmptyTask } from '../../use';
import { useStorymapStore } from '../../useModule';

const debug = createDebugger('pages:main:storymap:store:actions:newCard:createCardsFromRealtime');

const { registerReducer, registerMiddleware } = useStorymapStore();

export type CreatedCardSnapshotType = 'activity' | 'task' | 'subtask';

/**
 * The persisted state of one card created by the MCP `CreateStoryMapCards` tool, as the bridge sends it
 * (`CreateStoryMapCardsBridgeHandler.MapNextgenCard`).
 */
export interface CreatedCardSnapshot {
    type: CreatedCardSnapshotType;
    id: string;
    title: string;
    description: string;
    color: number;
    order: number;
    key?: string;
    activityId?: string;
    taskId?: string;
    releaseId?: string;
    assigneeUserId?: string;
    estimation?: number;
    status?: number;
    personaIds?: string[];
    annotationIds?: string[];
    basicPrioValue?: number;
    moscowPrioValue?: number;
    kanoPrioValue?: number;
    businessValueInt?: number;
    effortInt?: number;
    releaseViewOrder?: number;
    createdAt?: Date;
    createdByUserId?: string;
    lastUpdatedAt?: Date;
    lastUpdatedByUserId?: string;
}

/**
 * Cards of one `CreateStoryMapCards` call. The server sends them sorted by scope and ascending final persisted
 * order, so applying them one by one in the received order reproduces the saved orders (AF7).
 */
export class CreateCardsFromRealtime extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'CreateCardsFromRealtime';

    public constructor(public cards: CreatedCardSnapshot[] = []) {
        super(CreateCardsFromRealtime.ACTION_TYPE);
    }
}

/**
 * Applies one created card: a card already in the store is skipped before any sibling is shifted (a board that
 * loaded while the live messages were under way, or a repeated message); otherwise the siblings of its scope at or
 * after its order are shifted and the card is inserted.
 */
export const applyCreatedCard = (state: StorymapState, card: CreatedCardSnapshot): void => {
    if (getCardById(card.id)) {
        return;
    }

    if (card.type === 'activity') {
        state.activities.filter((x) => x.order >= card.order).forEach((x) => x.order++);

        const activity: Activity = GetNewEmptyActivity({
            id: card.id,
            title: card.title,
            description: card.description || '',
            color: card.color,
            order: card.order,
            assigneeUserId: card.assigneeUserId || null,
            key: card.key || null,
            isNew: false,
            personaIds: card.personaIds ?? [],
            annotationIds: card.annotationIds ?? [],
            hasDescription: card.description != null && card.description.trim() !== '',
            createdAt: card.createdAt || new Date(),
            createdByUserId: card.createdByUserId || null,
            lastUpdatedAt: card.lastUpdatedAt || null,
            lastUpdatedByUserId: card.lastUpdatedByUserId || null
        });

        state.activities.push(activity);
    }

    if (card.type === 'task') {
        state.tasks.filter((x) => x.activityId == card.activityId && x.order >= card.order).forEach((x) => x.order++);

        const task: Task = GetNewEmptyTask({
            id: card.id,
            activityId: card.activityId || null,
            title: card.title,
            description: card.description || '',
            color: card.color,
            order: card.order,
            assigneeUserId: card.assigneeUserId || null,
            key: card.key || null,
            isNew: false,
            personaIds: card.personaIds ?? [],
            annotationIds: card.annotationIds ?? [],
            hasDescription: card.description != null && card.description.trim() !== '',
            createdAt: card.createdAt || new Date(),
            createdByUserId: card.createdByUserId || null,
            lastUpdatedAt: card.lastUpdatedAt || null,
            lastUpdatedByUserId: card.lastUpdatedByUserId || null
        });

        state.tasks.push(task);
    }

    if (card.type === 'subtask') {
        state.subtasks
            .filter((x) => x.taskId == card.taskId && x.releaseId == card.releaseId && x.order >= card.order)
            .forEach((x) => x.order++);

        const subtask: Subtask = GetNewEmptySubtask({
            id: card.id,
            activityId: card.activityId || null,
            taskId: card.taskId || null,
            releaseId: card.releaseId || null,
            title: card.title,
            description: card.description || '',
            color: card.color,
            order: card.order,
            assigneeUserId: card.assigneeUserId || null,
            key: card.key || null,
            isNew: false,
            estimation: card.estimation,
            status: card.status ?? null,
            personaIds: card.personaIds ?? [],
            annotationIds: card.annotationIds ?? [],
            basicPrioValue: card.basicPrioValue ?? null,
            moscowPrioValue: card.moscowPrioValue ?? null,
            kanoPrioValue: card.kanoPrioValue ?? null,
            businessValue: card.businessValueInt ?? null,
            effort: card.effortInt ?? null,
            releaseViewOrder: card.releaseViewOrder ?? null,
            hasDescription: card.description != null && card.description.trim() !== '',
            createdAt: card.createdAt || new Date(),
            createdByUserId: card.createdByUserId || null,
            lastUpdatedAt: card.lastUpdatedAt || null,
            lastUpdatedByUserId: card.lastUpdatedByUserId || null
        });

        state.subtasks.push(subtask);
    }

    if (state.filterState.hideUnfiltered) {
        state.filterState.newlyCreatedCardIds.push(card.id);
    }
};

const middleware = async (): Promise<boolean> => {
    return true;
};

const reducer = (state: StorymapState, action: CreateCardsFromRealtime): void => {
    (action.cards ?? []).forEach((card) => {
        // One card's failure does not stop the rest (AF1a); the card appears after a reload.
        try {
            applyCreatedCard(state, card);
        } catch (e) {
            debug('Failed to apply the realtime created card %s: %o', card?.id, e);
        }
    });
};

//@ts-ignore
registerMiddleware(CreateCardsFromRealtime.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(CreateCardsFromRealtime.ACTION_TYPE, reducer);
